import { fmtDate } from '../../utils/datetime';
import React, { useState, useEffect } from "react";
import { IZone, ICountry } from "./utils/interface";
import { toast } from '../../utils/toast';
import * as Yup from "yup";
import { useFormik } from "formik";
import { useLoadScript } from "@react-google-maps/api";
import { Plus, Edit3, Trash2, User } from "lucide-react";
import PlacesAutocompleteInput from "./utils/PlacesAutocompleteInput";
import config from "../../config";
import { Modal } from "./utils/Modal";
import { createNewZone, deleteZone, editZone, getAllCountries, getAllZones } from "../../api/master";
import { useAuth } from "../../context/AuthContext";

type EditData = {
  zone_id: any;
  country_id: any;
  latitude: any;
  longitude: any;
};

const MyZone: React.FC = () => {
  const libraries: any = ["places"];
  const googleMapsApiKey: string = config.googleMapsApiKey;

  const token = localStorage.getItem("authToken");
  const { user } = useAuth()
  const [zones, setZones] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [editData, setEditData] = useState<EditData>({
    zone_id: "",
    country_id: "",
    latitude: "",
    longitude: "",
  });

  const [country, setCountry] = useState([]);

  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [placeName, setPlacename] = useState("");
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
    libraries: libraries,
  });

  useEffect(() => {
    if (user && user.tenant_id) {
      getZones();
      getCountries();
    }
  }, [user]);

  /**Add New Zone */
  const initialValues = {
    name: "",
    country_id: "",
  };

  const ZoneSchema = Yup.object({
    name: Yup.string().required("Please enter Zone Name"),
  });
  const {
    setValues,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
  } = useFormik({
    initialValues,
    validationSchema: ZoneSchema,
    onSubmit: async (values) => {
      if (!isEdit) {
        try {
          const response = await createNewZone(values.name, user.tenant_id, lat, lng, values.country_id)

          if (response.status === 200) {
            getZones();
            setIsAddModalOpen(false);
            setValues({ name: "", country_id: "" });
            toast.success("Zone Created Successfully!");
          }
        } catch (error: any) {
          toast.error(error.message || "Something went wrong.");
        }
      }

      if (isEdit) {
        try {
          const response = await editZone(values.name, user.tenant_id, lat, lng, values.country_id, editData.zone_id)

          if (response.status === 200) {
            getZones();
            setValues({ name: "", country_id: "" });
            setEditData({ ...editData, zone_id: 0 });
            setIsEdit(false);
            setIsEditModalOpen(false);
            toast.success("Zone Updated Successfully!");
          }
        } catch (error: any) {
          toast.error(error.message || "Something went wrong.");
        }
      }
    },
  });

  // console.log("designations", designation);
  const getZones = async () => {
    try {
      const response = await getAllZones(user.tenant_id);
      setZones(response);
    } catch (error) {
      console.log("Zone Error: ", error)
    }
  };
  const getCountries = async () => {
    try {
      const response = await getAllCountries(user.tenant_id);
      setCountry(response);
      console.log(country);
    } catch (error) { }
  };

  const handleEdit = (item: IZone) => {
    setValues({ name: item.name, country_id: item.country_id });
    setEditData({
      zone_id: item.id,
      country_id: item.country_id,
      latitude: item.latitude,
      longitude: item.longitude,
    });
    setLat(String(item.latitude ?? ""));
    setLng(String(item.longitude ?? ""));
    setPlacename(item.name ?? "");
    setIsEditModalOpen(true);
    setIsEdit(true);
  };

  const handleDelete = async (id: number) => {
    if (!await toast.confirm("This action cannot be undone.", "Delete zone?", "Delete")) return;
    try {
      await deleteZone(id);
      setZones(zones.filter((zone: IZone) => zone.id !== id));
      toast.success("Zone deleted successfully.");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete zone.");
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Zone Master</h1>
            <p className="text-blue-600 mt-1">Zone </p>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Zone
          </button>
        </div>

        {/* Master Data List */}
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6">
            {/* Header */}
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Zone ({zones.length})
            </h3>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      SN
                    </th>

                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Name
                    </th>

                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Latitude
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Longitude
                    </th>

                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Updated At
                    </th>

                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {zones.map((item: IZone, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {index + 1}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item.name}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item.latitude}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item.longitude}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {fmtDate(item.updated_at)}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                            title="Edit"
                            type="submit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add Modal */}
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title={`Add New Zone`}
        >
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Country *
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              name="country_id"
              value={values.country_id}
              onBlur={handleBlur}
              onChange={handleChange}
            >
              <option value="">--Select Site--</option>
              {country &&
                country.map((c: ICountry) => {
                  return (
                    <>
                      <option value={c.id}>{c.name}</option>
                    </>
                  );
                })}
            </select>
            {errors.country_id && touched.country_id && (
              <p className="text-red-500">{errors.country_id}</p>
            )}
            <form onSubmit={handleSubmit}>
              <div>
                {isLoaded ? (
                  <PlacesAutocompleteInput
                    onPlaceSelect={(name, lat, lng) => {
                      setLat(lat);
                      setLng(lng);
                      setPlacename(name);
                      setValues({ name, country_id: values.country_id });
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-500 mb-3">Loading maps...</p>
                )}

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  name="name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.name && touched.name && (
                  <p className="text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Zone
                </button>
              </div>
            </form>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title="Edit Zone"
        >
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Country *
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              name="country_id"
              value={values.country_id}
              onBlur={handleBlur}
              onChange={handleChange}
            >
              <option value="">--Select Site--</option>
              {country &&
                country.map((c: ICountry) => {
                  return (
                    <>
                      {c.id === editData.country_id ? (
                        <option value={c.id} selected>
                          {c.name}
                        </option>
                      ) : (
                        <option value={c.id}>{c.name}</option>
                      )}
                    </>
                  );
                })}
            </select>
            {errors.country_id && touched.country_id && (
              <p className="text-red-500">{errors.country_id}</p>
            )}
            <form onSubmit={handleSubmit}>
              <div>
                {isLoaded ? (
                  <PlacesAutocompleteInput
                    onPlaceSelect={(name, lat, lng) => {
                      setLat(lat);
                      setLng(lng);
                      setPlacename(name);
                      setValues({ name, country_id: values.country_id });
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-500 mb-3">Loading maps...</p>
                )}

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  name="name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.name && touched.name && (
                  <p className="text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default MyZone;
